// Uitsnede voor product- en categoriefoto's: een vierkant venster op de foto,
// genormaliseerd zodat het niet uitmaakt hoe groot de preview in de app is.
// De app stuurt alleen deze drie getallen mee met het originele bestand; de
// server past ze toe op de foto in volle resolutie en vult de rest van het
// vierkant met de achtergrond (go-server/internal/feature/beheer/square.go).
// De regels hieronder zijn die van de server; buiten het bereik geeft 422.
//
//   x, y   het midden van het venster als fractie van breedte en hoogte (0..1)
//   scale  de zijde van het venster als fractie van de lange zijde (0..1];
//          1 met het midden op (0.5, 0.5) is "Passend maken": de hele foto.

export type SquareCrop = { x: number; y: number; scale: number };

export type ImageSize = { width: number; height: number };

export const FIT: SquareCrop = { x: 0.5, y: 0.5, scale: 1 };

// minCropScale/minCropSide: zelfde grenzen als de server (20× zoom, venster
// minstens 100 pixels van het origineel).
const MIN_CROP_SCALE = 0.05;
const MIN_CROP_SIDE = 100;

export function minScale(size: ImageSize) {
  const full = Math.max(size.width, size.height);
  if (full <= 0) {
    return 1;
  }
  return Math.min(1, Math.max(MIN_CROP_SCALE, MIN_CROP_SIDE / full));
}

function clamp(v: number, lo: number, hi: number) {
  return Math.min(hi, Math.max(lo, v));
}

// clampCrop houdt het venster binnen het passende vierkant en het midden op
// de foto — precies wat de server accepteert.
export function clampCrop(c: SquareCrop, size: ImageSize): SquareCrop {
  const { width: w, height: h } = size;
  if (!(w > 0 && h > 0)) {
    return FIT;
  }
  const full = Math.max(w, h);
  const scale = clamp(Number.isFinite(c.scale) ? c.scale : 1, minScale(size), 1);
  const slack = (full - scale * full) / 2;
  const cx = clamp(clamp((Number.isFinite(c.x) ? c.x : 0.5) * w, w / 2 - slack, w / 2 + slack), 0, w);
  const cy = clamp(clamp((Number.isFinite(c.y) ? c.y : 0.5) * h, h / 2 - slack, h / 2 + slack), 0, h);
  return { x: cx / w, y: cy / h, scale };
}

export function isFit(c: SquareCrop) {
  return Math.abs(c.scale - 1) < 1e-6 && Math.abs(c.x - 0.5) < 1e-6 && Math.abs(c.y - 0.5) < 1e-6;
}

// zoomBy: inzoomen (factor > 1) of uitzoomen rond het huidige midden.
export function zoomBy(c: SquareCrop, factor: number, size: ImageSize): SquareCrop {
  return clampCrop({ ...c, scale: c.scale / factor }, size);
}

// panBy: het beeld dx/dy pixels (op het scherm) verschuiven in een vak van
// frame pixels; de foto beweegt mee met de vinger of muis.
export function panBy(c: SquareCrop, dx: number, dy: number, frame: number, size: ImageSize): SquareCrop {
  const full = Math.max(size.width, size.height);
  const d = frame / (c.scale * full); // schermpixels per originele pixel
  if (!(d > 0) || !(size.width > 0 && size.height > 0)) {
    return c;
  }
  return clampCrop({ ...c, x: c.x - dx / d / size.width, y: c.y - dy / d / size.height }, size);
}

// imageStyle: positie en maat van de foto in een vak van frame×frame
// pixels, zodat het vak precies het venster toont.
export function imageStyle(c: SquareCrop, frame: number, size: ImageSize) {
  const full = Math.max(size.width, size.height);
  const d = frame / (c.scale * full);
  return {
    position: "absolute" as const,
    width: size.width * d,
    height: size.height * d,
    left: frame / 2 - c.x * size.width * d,
    top: frame / 2 - c.y * size.height * d,
  };
}

// cropFields: de formuliervelden voor de upload (zes decimalen is ruim
// binnen de halve pixel die de server aan afronding toestaat).
export function cropFields(c: SquareCrop): [string, string][] {
  return [
    ["crop_x", c.x.toFixed(6)],
    ["crop_y", c.y.toFixed(6)],
    ["crop_scale", c.scale.toFixed(6)],
  ];
}
